// The Connections tab — T111 (23 Sep 2026), design doc § T111: the team's LinkedIn networks as
// people, each one somebody nobody elsewhere in the brain has become.
//
// THE LIST IS THE SERVICE'S, filters and sort included, as the Candidates list's is (T36): the tab
// can hold a whole network — fifteen thousand people for one recruiter — so a filter over the page
// the browser holds would miss exactly whoever is past it. The screen asks for a hundred and "Show
// more" asks for the next hundred, with the whole count beside it.
//
// A MOVE TO CANDIDATES IS TWO CALLS, as the entry form's is (T38): the request, then a settle the
// screen polls until the worker has made each record — and the settle, not the screen, puts them on
// the Search, carries their notes and takes them off the tab. A closed tab loses nothing: the next
// screen to open the tab settles whatever is still marked.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  ClientConnectionsResponse,
  ConnectionDetail,
  ConnectionFields,
  ConnectionMovesResponse,
  ConnectionPatch,
  ConnectionSettleResponse,
  ConnectionSort,
  ConnectionsImportOptionsResponse,
  ConnectionsImportPreviewResponse,
  ConnectionsImportResponse,
  ConnectionsResponse,
  ConnectionStagesResponse,
  ConnectionUploadsResponse,
  ContactTarget,
} from '@vera/bff';
import { API_PREFIX, query, type RequestFn } from './core.ts';
import { POLL_MS } from './intervals.ts';
import { KEYS, useConsoleMutation } from './mutation.ts';
import { useRequest } from './provider.tsx';

/** The list's filters and sort, as the URL holds them (screens/ConnectionsScreen.tsx). */
export interface ListConnectionsQuery {
  q?: string;
  /** A recruiter: only their network. */
  owner?: string;
  /** A stage's id, or `none` for no stage. */
  stage?: string;
  company?: string;
  sort?: ConnectionSort;
  limit?: number;
}

/** The upload, as both calls take it: the file's own bytes, never rows the browser made. */
export interface ConnectionsUpload {
  name: string;
  text?: string;
  workbook?: string;
  /** Whose network the file is — an active colleague's id (T111a); absent, the uploader's own. */
  ownerId?: string;
}

/** A page of the list — what "Show more" adds to. */
export const CONNECTIONS_PAGE = 100;

/** How often the tab asks the settle about a move in flight — a person is watching it land. */
export const MOVE_POLL_MS = 1_500;

export const connectionsApi = (request: RequestFn) => ({
  list: (options: ListConnectionsQuery = {}) =>
    request<ConnectionsResponse>(`${API_PREFIX}/connections${query({ ...options })}`),
  get: (connectionId: string) =>
    request<ConnectionDetail>(`${API_PREFIX}/connections/${connectionId}`),
  stages: async () =>
    (await request<ConnectionStagesResponse>(`${API_PREFIX}/connection-stages`)).stages,
  uploads: async () =>
    (await request<ConnectionUploadsResponse>(`${API_PREFIX}/connections/uploads`)).uploads,
  atClient: async (clientId: string) =>
    (await request<ClientConnectionsResponse>(`${API_PREFIX}/clients/${clientId}/connections`))
      .connections,

  create: async (fields: ConnectionFields) =>
    (await request<{ id: string }>(`${API_PREFIX}/connections`, { method: 'POST', body: fields }))
      .id,
  update: (connectionId: string, patch: ConnectionPatch) =>
    request<void>(`${API_PREFIX}/connections/${connectionId}`, { method: 'PATCH', body: patch }),
  setStage: async (connectionIds: string[], stageId: string | null) =>
    (
      await request<{ moved: number }>(`${API_PREFIX}/connections/stage`, {
        method: 'POST',
        body: { connectionIds, stageId },
      })
    ).moved,
  remove: async (connectionIds: string[]) =>
    (
      await request<{ deleted: number }>(`${API_PREFIX}/connections/delete`, {
        method: 'POST',
        body: { connectionIds },
      })
    ).deleted,
  addNote: async (connectionId: string, body: string) =>
    (
      await request<{ id: string }>(`${API_PREFIX}/connections/${connectionId}/notes`, {
        method: 'POST',
        body: { body },
      })
    ).id,
  deleteNote: (noteId: string) =>
    request<void>(`${API_PREFIX}/connection-notes/${noteId}`, { method: 'DELETE' }),

  toCandidates: (connectionIds: string[], searchId: string | null) =>
    request<ConnectionMovesResponse>(`${API_PREFIX}/connections/candidates`, {
      method: 'POST',
      body: { connectionIds, ...(searchId ? { searchId } : {}) },
    }),
  settle: (connectionIds: string[]) =>
    request<ConnectionSettleResponse>(`${API_PREFIX}/connections/settle`, {
      method: 'POST',
      body: { connectionIds },
    }),
  toContact: (connectionId: string, target: ContactTarget) =>
    request<{ clientId: string; contactId: string }>(
      `${API_PREFIX}/connections/${connectionId}/contact`,
      { method: 'POST', body: target },
    ),

  // --- the stage list ---
  createStage: async (name: string) =>
    (
      await request<{ id: string }>(`${API_PREFIX}/connection-stages`, {
        method: 'POST',
        body: { name },
      })
    ).id,
  renameStage: (stageId: string, name: string) =>
    request<void>(`${API_PREFIX}/connection-stages/${stageId}`, {
      method: 'PATCH',
      body: { name },
    }),
  reorderStages: (stageIds: string[]) =>
    request<void>(`${API_PREFIX}/connection-stages/order`, {
      method: 'PUT',
      body: { stageIds },
    }),
  /** `moveTo` is where its connections go: another stage, or null for none. */
  deleteStage: (stageId: string, moveTo?: string | null) =>
    request<void>(
      `${API_PREFIX}/connection-stages/${stageId}${moveTo === undefined ? '' : `?moveTo=${moveTo ?? 'none'}`}`,
      { method: 'DELETE' },
    ),

  // --- the upload ---
  importOptions: () =>
    request<ConnectionsImportOptionsResponse>(`${API_PREFIX}/connections/import/options`),
  preview: (upload: ConnectionsUpload) =>
    request<ConnectionsImportPreviewResponse>(`${API_PREFIX}/connections/import/preview`, {
      method: 'POST',
      body: upload,
    }),
  /** The same file again, planned against the brain at the tap, with the name matches decided. */
  confirm: (upload: ConnectionsUpload & { asNew: number[] }) =>
    request<ConnectionsImportResponse>(`${API_PREFIX}/connections/import`, {
      method: 'POST',
      body: upload,
    }),
});

export function useConnections(
  options: ListConnectionsQuery = {},
): UseQueryResult<ConnectionsResponse> {
  const request = useRequest();
  return useQuery({
    queryKey: ['connections', options],
    queryFn: () => connectionsApi(request).list(options),
    refetchInterval: POLL_MS.slow,
    // "Show more" asks for a longer page: the rows already drawn stay while it comes.
    placeholderData: (previous) => previous,
  });
}

/** One connection, for the panel. Off while none is open. */
export function useConnection(connectionId: string | null): UseQueryResult<ConnectionDetail> {
  const request = useRequest();
  return useQuery({
    queryKey: ['connection', connectionId],
    queryFn: () => connectionsApi(request).get(connectionId!),
    refetchInterval: POLL_MS.slow,
    enabled: connectionId !== null,
  });
}

export function useConnectionStages() {
  const request = useRequest();
  return useQuery({
    queryKey: ['connectionStages'],
    queryFn: () => connectionsApi(request).stages(),
    refetchInterval: POLL_MS.slow,
  });
}

export function useConnectionUploads() {
  const request = useRequest();
  return useQuery({
    queryKey: ['connectionUploads'],
    queryFn: () => connectionsApi(request).uploads(),
    refetchInterval: POLL_MS.slow,
  });
}

/** The company page's "Connections who work there". */
export function useClientConnections(clientId: string) {
  const request = useRequest();
  return useQuery({
    queryKey: ['clientConnections', clientId],
    queryFn: () => connectionsApi(request).atClient(clientId),
    refetchInterval: POLL_MS.slow,
    enabled: clientId !== '',
  });
}

export function useConnectionsImportOptions(enabled: boolean) {
  const request = useRequest();
  return useQuery({
    queryKey: ['connectionsImportOptions'],
    queryFn: () => connectionsApi(request).importOptions(),
    enabled,
    staleTime: Infinity,
  });
}

export const useCreateConnection = () =>
  useConsoleMutation(
    (request, fields: ConnectionFields) => connectionsApi(request).create(fields),
    KEYS.connection,
  );

export const useUpdateConnection = () =>
  useConsoleMutation(
    (request, args: { connectionId: string; patch: ConnectionPatch }) =>
      connectionsApi(request).update(args.connectionId, args.patch),
    KEYS.connection,
  );

export const useSetConnectionsStage = () =>
  useConsoleMutation(
    (request, args: { connectionIds: string[]; stageId: string | null }) =>
      connectionsApi(request).setStage(args.connectionIds, args.stageId),
    KEYS.connection,
  );

export const useDeleteConnections = () =>
  useConsoleMutation(
    (request, connectionIds: string[]) => connectionsApi(request).remove(connectionIds),
    KEYS.connection,
  );

export const useAddConnectionNote = () =>
  useConsoleMutation(
    (request, args: { connectionId: string; body: string }) =>
      connectionsApi(request).addNote(args.connectionId, args.body),
    KEYS.connection,
  );

export const useDeleteConnectionNote = () =>
  useConsoleMutation(
    (request, noteId: string) => connectionsApi(request).deleteNote(noteId),
    KEYS.connection,
  );

export const useConnectionsToCandidates = () =>
  useConsoleMutation(
    (request, args: { connectionIds: string[]; searchId: string | null }) =>
      connectionsApi(request).toCandidates(args.connectionIds, args.searchId),
    KEYS.connectionMove,
  );

export const useSettleConnectionMoves = () =>
  useConsoleMutation(
    (request, connectionIds: string[]) => connectionsApi(request).settle(connectionIds),
    KEYS.connectionMove,
  );

export const useConnectionToContact = () =>
  useConsoleMutation(
    (request, args: { connectionId: string; target: ContactTarget }) =>
      connectionsApi(request).toContact(args.connectionId, args.target),
    KEYS.connectionMove,
  );

export const useCreateConnectionStage = () =>
  useConsoleMutation(
    (request, name: string) => connectionsApi(request).createStage(name),
    KEYS.connection,
  );

export const useRenameConnectionStage = () =>
  useConsoleMutation(
    (request, args: { stageId: string; name: string }) =>
      connectionsApi(request).renameStage(args.stageId, args.name),
    KEYS.connection,
  );

export const useReorderConnectionStages = () =>
  useConsoleMutation(
    (request, stageIds: string[]) => connectionsApi(request).reorderStages(stageIds),
    KEYS.connection,
  );

export const useDeleteConnectionStage = () =>
  useConsoleMutation(
    (request, args: { stageId: string; moveTo?: string | null }) =>
      connectionsApi(request).deleteStage(args.stageId, args.moveTo),
    KEYS.connection,
  );

export const useConnectionsImportPreview = () =>
  useConsoleMutation(
    (request, upload: ConnectionsUpload) => connectionsApi(request).preview(upload),
    [],
  );

export const useConfirmConnectionsImport = () =>
  useConsoleMutation(
    (request, upload: ConnectionsUpload & { asNew: number[] }) =>
      connectionsApi(request).confirm(upload),
    KEYS.connection,
  );
